import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FormFieldComponent } from '../../../shared/form-field/form-field.component';
import { FieldInfoToggleComponent } from '../../../shared/field-info/field-info-toggle.component';
import { FieldInfoService } from '../../../shared/field-info/field-info.service';
import { CupomService, SalvarCupomPayload } from '../../../core/cupom/cupom.service';
import { CatalogoService } from '../../../core/catalogo/catalogo.service';
import { ClassificacaoItem, ProcedimentoItem, PlanoB2cItem, EmpresaItem, EstabelecimentoItem } from '../../../core/catalogo/catalogo.model';
import { RegraProcedimentoService } from '../../../core/regra-procedimento/regra-procedimento.service';
import { SalvarRegraPayload } from '../../../core/regra-procedimento/regra-procedimento.model';
import { MatrizValorService } from '../../../core/matriz-valor/matriz-valor.service';
import { SalvarMatrizPayload } from '../../../core/matriz-valor/matriz-valor.model';
import { RegraPlanoService } from '../../../core/regra-plano/regra-plano.service';
import { RegraEstabelecimentoService } from '../../../core/regra-estabelecimento/regra-estabelecimento.service';
import { RegraEmpresaService } from '../../../core/regra-empresa/regra-empresa.service';
import { CupomComboService } from '../../../core/cupom-combo/cupom-combo.service';
import { RegraDependenciaService } from '../../../core/regra-dependencia/regra-dependencia.service';
import { CupomListado } from '../../../core/cupom/cupom.model';
import { CUPOM_WIZARD_FIELD_INFO } from './cupom-wizard.field-info';

interface OptCard {
  value: string;
  title: string;
  desc: string;
}

interface StepDef {
  num: number;
  label: string;
  optional: boolean;
}

const TIPO_OPTIONS: OptCard[] = [
  { value: 'P', title: 'Todos', desc: 'Atende qualquer cliente' },
  { value: 'C', title: 'B2C', desc: 'Assinante CartaoSIM' },
  { value: 'E', title: 'Empresa', desc: 'B2B — empresa parceira' },
  { value: 'A', title: 'Assinantes', desc: 'B2C + B2B combinados' },
];

const DESCONTO_OPTIONS: OptCard[] = [
  { value: 'P', title: 'Percentual', desc: '% sobre o total' },
  { value: 'F', title: 'Preço fixo', desc: 'Cobrar valor X' },
  { value: 'R', title: 'Real fixo', desc: 'R$ de desconto' },
  { value: 'V', title: 'Voucher', desc: '100% grátis' },
];

const STEPS: StepDef[] = [
  { num: 1, label: 'Básico', optional: false },
  { num: 2, label: 'Procedimentos', optional: false },
  { num: 3, label: 'Preço', optional: true },
  { num: 4, label: 'Filtros', optional: true },
  { num: 5, label: 'Combo', optional: true },
  { num: 6, label: 'Revisão', optional: false },
];

type SwitchKey = 'plano' | 'emp' | 'estab' | 'prof' | 'idade' | 'pai' | 'dep';

@Component({
  selector: 'app-cupom-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FormFieldComponent, FieldInfoToggleComponent],
  templateUrl: './cupom-wizard.component.html',
  styleUrl: './cupom-wizard.component.scss',
})
export class CupomWizardComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly fieldInfo = inject(FieldInfoService);
  private readonly cupomSvc = inject(CupomService);
  private readonly catalogoSvc = inject(CatalogoService);
  private readonly regraSvc = inject(RegraProcedimentoService);
  private readonly matrizSvc = inject(MatrizValorService);
  private readonly regraPlanoSvc = inject(RegraPlanoService);
  private readonly regraEstabSvc = inject(RegraEstabelecimentoService);
  private readonly regraEmpresaSvc = inject(RegraEmpresaService);
  private readonly comboSvc = inject(CupomComboService);
  private readonly depSvc = inject(RegraDependenciaService);
  private readonly router = inject(Router);

  protected readonly salvando = signal(false);
  protected readonly erroSalvar = signal<string | null>(null);

  // Step 2 — Procedimentos
  readonly classificacoes = signal<ClassificacaoItem[]>([]);
  readonly regrasClassif = signal<{ nrSeqClassif: number; dsClassificacao: string; ieModo: string }[]>([]);
  readonly regrasIndividuais = signal<{ cdProcedimento: number; dsProcedimento: string; ieModo: string }[]>([]);
  readonly classifSelecionada = signal<number | null>(null);
  readonly classifBusca = signal('');
  readonly buscaProc = signal('');
  readonly resultadosBusca = signal<ProcedimentoItem[]>([]);
  readonly buscando = signal(false);

  // Autocomplete da classificacao: filtra (client-side) as disponiveis pelo texto digitado.
  readonly classifFiltradas = computed(() => {
    const q = this.classifBusca().trim().toLowerCase();
    const base = this.classifDisponiveis;
    const lista = q ? base.filter(c => c.dsClassificacao.toLowerCase().includes(q)) : base;
    return lista.slice(0, 50);
  });
  private procTimer: ReturnType<typeof setTimeout> | null = null;

  // Step 3 — Matriz de valor (faixas de preco)
  readonly faixas = signal<{ nrOrdem: number; dsRegra: string; ieExigeCartaoSim: string; prDesconto: number | null; vlFixo: number | null; vlTeto: number | null; filtros: { ieTipo: string; cdValor: number }[] }[]>([]);

  // Step 4 — Filtros
  readonly planosB2c = signal<PlanoB2cItem[]>([]);
  readonly empresas = signal<EmpresaItem[]>([]);
  readonly estabelecimentos = signal<EstabelecimentoItem[]>([]);
  readonly regrasPlano = signal<{ cdPlan: number; dsPlano: string; ieModo: string }[]>([]);
  readonly regrasEmpresa = signal<{ cdEmpresa: number; dsEmpresa: string; ieModo: string }[]>([]);
  readonly regrasEstabelecimento = signal<{ cdEstabelecimento: number; dsEstabelecimento: string; ieModo: string }[]>([]);
  readonly planoSelecionado = signal<number | null>(null);
  readonly empresaSelecionada = signal<number | null>(null);
  readonly estabSelecionado = signal<number | null>(null);

  // Step 5 — Combo e dependencias
  readonly combos = signal<{ nrSeqCupomFilho: number; codCupom: string; nrOrdem: number; nrDiasOffsetLiberacao: number; nrDiasValidade: number | null; ieRecorrente: string; qtRepeticoes: number | null }[]>([]);
  readonly dependencias = signal<{ nrSeqCupomDep: number; codCupom: string; ieTipoDep: string; nrDiasValidade: number | null }[]>([]);
  readonly buscaCupomCombo = signal('');
  readonly buscaCupomDep = signal('');
  readonly resultadosBuscaCupom = signal<CupomListado[]>([]);
  readonly buscandoCupom = signal(false);

  readonly STEPS = STEPS;
  readonly TIPO_OPTIONS = TIPO_OPTIONS;
  readonly DESCONTO_OPTIONS = DESCONTO_OPTIONS;

  readonly currentStep = signal(1);
  readonly erroPasso = signal<string | null>(null);

  readonly switches = signal<Record<SwitchKey, boolean>>({
    plano: false,
    emp: false,
    estab: false,
    prof: false,
    idade: false,
    pai: false,
    dep: false,
  });

  readonly form: FormGroup = this.fb.group({
    codigo: ['', [Validators.required]],
    descricao: ['', [Validators.required]],
    observacao: [''],
    emissaoManual: [false],
    tipo: ['P', [Validators.required]],
    tipoDesconto: ['P', [Validators.required]],
    percentual: [null],
    valorDesconto: [{ value: null, disabled: true }],
    tetoDesconto: [null],
    dtInicio: ['', [Validators.required]],
    dtFim: [null],
    qtMaxUsos: [null],
    qtPorPessoa: [1],
    bloqueioDias: [null],
    beneficiario: ['T', [Validators.required]],
    idadeMin: [null],
    idadeMax: [null],
  });

  readonly isLastStep = computed(() => this.currentStep() === STEPS.length);
  readonly isFirstStep = computed(() => this.currentStep() === 1);
  readonly currentStepDef = computed(() => STEPS[this.currentStep() - 1]);

  ngOnInit(): void {
    this.fieldInfo.register(CUPOM_WIZARD_FIELD_INFO);
    this.carregarClassificacoes();
  }

  cancelar(): void {
    this.router.navigateByUrl('/cupons');
  }

  goTo(n: number): void {
    if (n < 1 || n > STEPS.length) return;
    // Bloqueia sair do passo 1 (Basico) sem os campos obrigatorios preenchidos.
    if (n > 1 && this.form.invalid) {
      this.form.markAllAsTouched();
      const faltam: string[] = [];
      if (this.form.get('codigo')?.invalid)    faltam.push('Código');
      if (this.form.get('descricao')?.invalid) faltam.push('Descrição');
      if (this.form.get('dtInicio')?.invalid)  faltam.push('Vigência inicial');
      this.erroPasso.set('Preencha os campos obrigatórios antes de avançar' +
        (faltam.length ? ': ' + faltam.join(', ') + '.' : '.'));
      this.currentStep.set(1);
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    this.erroPasso.set(null);
    this.currentStep.set(n);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  isStepDone(n: number): boolean {
    return n < this.currentStep();
  }

  isStepActive(n: number): boolean {
    return n === this.currentStep();
  }

  selectTipo(value: string): void {
    this.form.patchValue({ tipo: value });
  }

  selectTipoDesconto(value: string): void {
    this.form.patchValue({ tipoDesconto: value });
    const percentual = this.form.controls['percentual'];
    const valor = this.form.controls['valorDesconto'];
    if (value === 'P') {
      percentual.enable();
      valor.disable();
    } else if (value === 'V') {
      percentual.disable();
      valor.disable();
    } else {
      percentual.disable();
      valor.enable();
    }
  }

  toggleSwitch(key: SwitchKey): void {
    this.switches.update((s) => ({ ...s, [key]: !s[key] }));
  }

  isSwitchOn(key: SwitchKey): boolean {
    return this.switches()[key];
  }

  // ---- Step 2: Procedimentos ----

  carregarClassificacoes(): void {
    this.catalogoSvc.listarClassificacoes().subscribe({
      next: res => {
        if (res.data) this.classificacoes.set(res.data);
      },
    });
  }

  adicionarClassificacao(): void {
    const sel = this.classifSelecionada();
    if (sel == null) return;
    if (this.regrasClassif().some(r => r.nrSeqClassif === sel)) return;
    const cat = this.classificacoes().find(c => c.nrSeqClassif === sel);
    if (!cat) return;
    this.regrasClassif.update(arr => [...arr, {
      nrSeqClassif: cat.nrSeqClassif,
      dsClassificacao: cat.dsClassificacao,
      ieModo: 'I',
    }]);
    this.classifSelecionada.set(null);
  }

  // Selecao via autocomplete: adiciona direto a classificacao clicada e limpa a busca.
  selecionarClassif(cat: ClassificacaoItem): void {
    this.classifSelecionada.set(cat.nrSeqClassif);
    this.adicionarClassificacao();
    this.classifBusca.set('');
  }

  removerClassificacao(nrSeqClassif: number): void {
    this.regrasClassif.update(arr => arr.filter(r => r.nrSeqClassif !== nrSeqClassif));
  }

  // Autocomplete do procedimento: busca server-side com debounce conforme digita.
  onBuscaProcChange(termo: string): void {
    this.buscaProc.set(termo);
    if (this.procTimer) clearTimeout(this.procTimer);
    if (termo.trim().length < 2) {
      this.resultadosBusca.set([]);
      return;
    }
    this.procTimer = setTimeout(() => this.buscarProcedimentos(), 300);
  }

  buscarProcedimentos(): void {
    const termo = this.buscaProc().trim();
    if (termo.length < 2) {
      this.resultadosBusca.set([]);
      return;
    }
    this.buscando.set(true);
    this.catalogoSvc.buscarProcedimentos(termo, undefined, 20).subscribe({
      next: res => {
        this.buscando.set(false);
        if (res.data) this.resultadosBusca.set(res.data);
      },
      error: () => this.buscando.set(false),
    });
  }

  adicionarProcedimento(proc: ProcedimentoItem): void {
    if (this.regrasIndividuais().some(r => r.cdProcedimento === proc.cdProcedimento)) return;
    this.regrasIndividuais.update(arr => [...arr, {
      cdProcedimento: proc.cdProcedimento,
      dsProcedimento: proc.dsProcedimento,
      ieModo: 'I',
    }]);
    this.resultadosBusca.set([]);
    this.buscaProc.set('');
  }

  removerProcedimento(cdProcedimento: number): void {
    this.regrasIndividuais.update(arr => arr.filter(r => r.cdProcedimento !== cdProcedimento));
  }

  get classifDisponiveis(): ClassificacaoItem[] {
    const usadas = new Set(this.regrasClassif().map(r => r.nrSeqClassif));
    return this.classificacoes().filter(c => !usadas.has(c.nrSeqClassif));
  }

  get resumoProcsLabel(): string {
    const classifs = this.regrasClassif();
    const indivs = this.regrasIndividuais();
    const parts: string[] = [];
    if (classifs.length > 0) {
      parts.push(classifs.map(c => c.dsClassificacao).join(', '));
    }
    if (indivs.length > 0) {
      parts.push(`${indivs.length} procedimento(s) individual(is)`);
    }
    return parts.length > 0 ? parts.join(' + ') : 'Nenhum procedimento configurado';
  }

  // ---- Step 3: Matriz de valor ----

  adicionarFaixa(dsRegra: string, prDesconto: number | null, vlFixo: number | null, vlTeto: number | null, ieExigeCartaoSim: string): void {
    const ordem = this.faixas().length + 1;
    this.faixas.update(arr => [...arr, { nrOrdem: ordem, dsRegra, ieExigeCartaoSim, prDesconto, vlFixo, vlTeto, filtros: [] }]);
  }

  removerFaixa(nrOrdem: number): void {
    this.faixas.update(arr => {
      const filtered = arr.filter(f => f.nrOrdem !== nrOrdem);
      return filtered.map((f, i) => ({ ...f, nrOrdem: i + 1 }));
    });
  }

  get resumoMatrizLabel(): string {
    const f = this.faixas();
    if (f.length === 0) return 'Sem faixa de preco';
    return `${f.length} faixa(s) configurada(s)`;
  }

  // ---- Step 4: Filtros ----

  carregarPlanosB2c(): void {
    if (this.planosB2c().length > 0) return;
    this.catalogoSvc.listarPlanosB2c().subscribe({
      next: res => { if (res.data) this.planosB2c.set(res.data); },
    });
  }

  carregarEmpresas(): void {
    if (this.empresas().length > 0) return;
    this.catalogoSvc.listarEmpresas().subscribe({
      next: res => { if (res.data) this.empresas.set(res.data); },
    });
  }

  carregarEstabelecimentos(): void {
    if (this.estabelecimentos().length > 0) return;
    this.catalogoSvc.listarEstabelecimentos().subscribe({
      next: res => { if (res.data) this.estabelecimentos.set(res.data); },
    });
  }

  toggleSwitchFiltro(key: SwitchKey): void {
    this.toggleSwitch(key);
    if (key === 'plano' && this.isSwitchOn('plano')) this.carregarPlanosB2c();
    if (key === 'emp' && this.isSwitchOn('emp')) this.carregarEmpresas();
    if (key === 'estab' && this.isSwitchOn('estab')) this.carregarEstabelecimentos();
  }

  adicionarPlano(): void {
    const sel = this.planoSelecionado();
    if (sel == null) return;
    if (this.regrasPlano().some(r => r.cdPlan === sel)) return;
    const plano = this.planosB2c().find(p => p.cdPlano === sel);
    if (!plano) return;
    this.regrasPlano.update(arr => [...arr, { cdPlan: plano.cdPlano, dsPlano: plano.dsPlano, ieModo: 'I' }]);
    this.planoSelecionado.set(null);
  }

  removerPlano(cdPlan: number): void {
    this.regrasPlano.update(arr => arr.filter(r => r.cdPlan !== cdPlan));
  }

  get planosDisponiveis(): PlanoB2cItem[] {
    const usados = new Set(this.regrasPlano().map(r => r.cdPlan));
    return this.planosB2c().filter(p => !usados.has(p.cdPlano));
  }

  adicionarEmpresa(): void {
    const sel = this.empresaSelecionada();
    if (sel == null) return;
    if (this.regrasEmpresa().some(r => r.cdEmpresa === sel)) return;
    const emp = this.empresas().find(e => e.cdEmpresa === sel);
    if (!emp) return;
    this.regrasEmpresa.update(arr => [...arr, { cdEmpresa: emp.cdEmpresa, dsEmpresa: emp.dsEmpresa, ieModo: 'I' }]);
    this.empresaSelecionada.set(null);
  }

  removerEmpresa(cdEmpresa: number): void {
    this.regrasEmpresa.update(arr => arr.filter(r => r.cdEmpresa !== cdEmpresa));
  }

  get empresasDisponiveis(): EmpresaItem[] {
    const usadas = new Set(this.regrasEmpresa().map(r => r.cdEmpresa));
    return this.empresas().filter(e => !usadas.has(e.cdEmpresa));
  }

  adicionarEstabelecimento(): void {
    const sel = this.estabSelecionado();
    if (sel == null) return;
    if (this.regrasEstabelecimento().some(r => r.cdEstabelecimento === sel)) return;
    const est = this.estabelecimentos().find(e => e.cdEstabelecimento === sel);
    if (!est) return;
    this.regrasEstabelecimento.update(arr => [...arr, { cdEstabelecimento: est.cdEstabelecimento, dsEstabelecimento: est.dsEstabelecimento, ieModo: 'I' }]);
    this.estabSelecionado.set(null);
  }

  removerEstabelecimento(cdEstabelecimento: number): void {
    this.regrasEstabelecimento.update(arr => arr.filter(r => r.cdEstabelecimento !== cdEstabelecimento));
  }

  get estabDisponiveis(): EstabelecimentoItem[] {
    const usados = new Set(this.regrasEstabelecimento().map(r => r.cdEstabelecimento));
    return this.estabelecimentos().filter(e => !usados.has(e.cdEstabelecimento));
  }

  get resumoFiltrosLabel(): string {
    const parts: string[] = [];
    if (this.regrasPlano().length > 0) parts.push(`${this.regrasPlano().length} plano(s)`);
    if (this.regrasEmpresa().length > 0) parts.push(`${this.regrasEmpresa().length} empresa(s)`);
    if (this.regrasEstabelecimento().length > 0) parts.push(`${this.regrasEstabelecimento().length} estabelecimento(s)`);
    return parts.length > 0 ? parts.join(', ') : '';
  }

  // ---- Step 5: Combo e dependencia ----

  buscarCupons(target: 'combo' | 'dep'): void {
    const termo = target === 'combo' ? this.buscaCupomCombo().trim() : this.buscaCupomDep().trim();
    if (termo.length < 2) {
      this.resultadosBuscaCupom.set([]);
      return;
    }
    this.buscandoCupom.set(true);
    this.cupomSvc.listar({ search: termo, take: 10 }).subscribe({
      next: res => {
        this.buscandoCupom.set(false);
        if (res.data) this.resultadosBuscaCupom.set(res.data.items ?? []);
      },
      error: () => this.buscandoCupom.set(false),
    });
  }

  adicionarCombo(cupom: CupomListado): void {
    if (this.combos().some(c => c.nrSeqCupomFilho === cupom.nrSequencia)) return;
    const ordem = this.combos().length + 1;
    this.combos.update(arr => [...arr, {
      nrSeqCupomFilho: cupom.nrSequencia,
      codCupom: cupom.codCupom,
      nrOrdem: ordem,
      nrDiasOffsetLiberacao: 0,
      nrDiasValidade: null,
      ieRecorrente: 'N',
      qtRepeticoes: null,
    }]);
    this.resultadosBuscaCupom.set([]);
    this.buscaCupomCombo.set('');
  }

  // Offset de liberacao (dias) do cupom-filho — editavel direto na tabela do combo.
  setComboOffset(nrSeqCupomFilho: number, dias: unknown): void {
    const n = Math.max(0, Math.floor(Number(dias) || 0));
    this.combos.update(arr => arr.map(c =>
      c.nrSeqCupomFilho === nrSeqCupomFilho ? { ...c, nrDiasOffsetLiberacao: n } : c));
  }

  removerCombo(nrSeqCupomFilho: number): void {
    this.combos.update(arr => {
      const filtered = arr.filter(c => c.nrSeqCupomFilho !== nrSeqCupomFilho);
      return filtered.map((c, i) => ({ ...c, nrOrdem: i + 1 }));
    });
  }

  adicionarDependencia(cupom: CupomListado): void {
    if (this.dependencias().some(d => d.nrSeqCupomDep === cupom.nrSequencia)) return;
    this.dependencias.update(arr => [...arr, {
      nrSeqCupomDep: cupom.nrSequencia,
      codCupom: cupom.codCupom,
      ieTipoDep: 'U',
      nrDiasValidade: null,
    }]);
    this.resultadosBuscaCupom.set([]);
    this.buscaCupomDep.set('');
  }

  removerDependencia(nrSeqCupomDep: number): void {
    this.dependencias.update(arr => arr.filter(d => d.nrSeqCupomDep !== nrSeqCupomDep));
  }

  get resumoComboLabel(): string {
    const parts: string[] = [];
    if (this.combos().length > 0) parts.push(`${this.combos().length} cupom(ns) filho(s)`);
    if (this.dependencias().length > 0) parts.push(`${this.dependencias().length} dependencia(s)`);
    return parts.length > 0 ? parts.join(', ') : '';
  }

  // Helpers para a tela de revisão
  get codigo(): string {
    return this.form.value.codigo ?? '';
  }
  get descricao(): string {
    return this.form.value.descricao ?? '';
  }
  get tipoLabel(): string {
    const v = this.form.value.tipo;
    return TIPO_OPTIONS.find((o) => o.value === v)?.title ?? '';
  }
  get tipoDescontoLabel(): string {
    const v = this.form.value.tipoDesconto;
    const opt = DESCONTO_OPTIONS.find((o) => o.value === v);
    if (!opt) return '';
    if (v === 'P') return `Percentual — ${this.form.value.percentual ?? 0}%`;
    if (v === 'V') return 'Voucher — 100% grátis';
    return `${opt.title} — R$ ${this.form.value.valorDesconto ?? 0}`;
  }
  get vigenciaLabel(): string {
    const ini = this.form.value.dtInicio ? this.formatDate(this.form.value.dtInicio) : '—';
    const fim = this.form.value.dtFim ? this.formatDate(this.form.value.dtFim) : 'sem prazo';
    return `${ini} → ${fim}`;
  }
  get limitesLabel(): string {
    const por = this.form.value.qtPorPessoa ?? 1;
    const total = this.form.value.qtMaxUsos
      ? `teto global ${this.form.value.qtMaxUsos}`
      : 'sem teto global';
    return `${por} uso por pessoa, ${total}`;
  }

  private formatDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  // ------------------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------------------
  salvar(): void {
    if (this.salvando()) return;

    if (!this.form.value.codigo || !this.form.value.descricao || !this.form.value.tipo
        || !this.form.value.tipoDesconto || !this.form.value.dtInicio) {
      this.erroSalvar.set('Preencha código, descrição, tipo, tipo de desconto e início da vigência.');
      this.goTo(1);
      return;
    }

    const v = this.form.getRawValue();
    const tipoDesc = v.tipoDesconto as string;

    const payload: SalvarCupomPayload = {
      codCupom: (v.codigo as string).toUpperCase(),
      dsCupom: v.descricao as string,
      dsObservacao: (v.observacao as string) || null,
      ieTipo: v.tipo as string,
      ieTipoDesconto: tipoDesc,
      prDesconto: tipoDesc === 'P' ? this.toNum(v.percentual) : null,
      vlDesconto: (tipoDesc === 'F' || tipoDesc === 'R') ? this.toNum(v.valorDesconto) : null,
      vlTeto: this.toNum(v.tetoDesconto),
      dtVigenciaIni: v.dtInicio as string,
      dtVigenciaFim: (v.dtFim as string) || null,
      qtUsoMaxGlobal: this.toInt(v.qtMaxUsos),
      qtUsoMaxPessoa: this.toInt(v.qtPorPessoa) ?? 1,
      nrDiasBloqueioReuso: this.toInt(v.bloqueioDias),
      ieBeneficiario: (v.beneficiario as string) || 'T',
      iePaiCombo: this.isSwitchOn('pai') ? 'S' : 'N',
      ieEmissaoManual: (v.emissaoManual as boolean) ? 'S' : 'N',
      iePermiteSupForaVigencia: 'N',
      ieSituacao: 'A',
    };

    this.salvando.set(true);
    this.erroSalvar.set(null);
    this.cupomSvc.criar(payload).subscribe({
      next: res => {
        if (res.status >= 200 && res.status < 300 && res.data) {
          this.salvarRegras(res.data);
        } else {
          this.salvando.set(false);
          this.erroSalvar.set(res.error?.message ?? 'Falha ao salvar cupom.');
        }
      },
      error: e => {
        this.salvando.set(false);
        const msg = e?.error?.error?.message ?? e?.error?.title ?? e?.message ?? 'Falha ao salvar cupom.';
        this.erroSalvar.set(msg);
      },
    });
  }

  private salvarRegras(nrSeqCupom: number): void {
    const regras: SalvarRegraPayload[] = [
      ...this.regrasClassif().map(r => ({ nrSeqClassif: r.nrSeqClassif, ieModo: r.ieModo })),
      ...this.regrasIndividuais().map(r => ({ cdProcedimento: r.cdProcedimento, ieModo: r.ieModo })),
    ];

    const total = regras.length
      + this.faixas().length
      + this.regrasPlano().length
      + this.regrasEmpresa().length
      + this.regrasEstabelecimento().length
      + this.combos().length
      + this.dependencias().length;

    if (total === 0) {
      this.salvando.set(false);
      this.router.navigateByUrl('/cupons');
      return;
    }

    let concluidas = 0;
    let erros: string[] = [];

    const checkDone = () => {
      if (concluidas === total) {
        this.salvando.set(false);
        if (erros.length > 0) {
          this.erroSalvar.set(`Cupom criado, mas ${erros.length} item(ns) falharam.`);
        } else {
          this.router.navigateByUrl('/cupons');
        }
      }
    };

    const ok = () => { concluidas++; checkDone(); };
    const fail = (tag: string) => () => { concluidas++; erros.push(tag); checkDone(); };

    for (const regra of regras) {
      this.regraSvc.criar(nrSeqCupom, regra).subscribe({ next: ok, error: fail('regra') });
    }

    for (const faixa of this.faixas()) {
      const payload: SalvarMatrizPayload = {
        nrOrdem: faixa.nrOrdem,
        dsRegra: faixa.dsRegra,
        ieExigeCartaoSim: faixa.ieExigeCartaoSim,
        prDesconto: faixa.prDesconto,
        vlFixo: faixa.vlFixo,
        vlTeto: faixa.vlTeto,
        filtros: faixa.filtros,
      };
      this.matrizSvc.criar(nrSeqCupom, payload).subscribe({ next: ok, error: fail('matriz') });
    }

    for (const rp of this.regrasPlano()) {
      this.regraPlanoSvc.criar(nrSeqCupom, { cdPlan: rp.cdPlan, ieModo: rp.ieModo }).subscribe({ next: ok, error: fail('plano') });
    }

    for (const re of this.regrasEmpresa()) {
      this.regraEmpresaSvc.criar(nrSeqCupom, { cdEmpresa: re.cdEmpresa, ieModo: re.ieModo }).subscribe({ next: ok, error: fail('empresa') });
    }

    for (const rest of this.regrasEstabelecimento()) {
      this.regraEstabSvc.criar(nrSeqCupom, { cdEstabelecimento: rest.cdEstabelecimento, ieModo: rest.ieModo }).subscribe({ next: ok, error: fail('estab') });
    }

    for (const combo of this.combos()) {
      this.comboSvc.criar(nrSeqCupom, {
        nrSeqCupomFilho: combo.nrSeqCupomFilho,
        nrOrdem: combo.nrOrdem,
        nrDiasOffsetLiberacao: combo.nrDiasOffsetLiberacao,
        nrDiasValidade: combo.nrDiasValidade,
        ieRecorrente: combo.ieRecorrente,
        qtRepeticoes: combo.qtRepeticoes,
      }).subscribe({ next: ok, error: fail('combo') });
    }

    for (const dep of this.dependencias()) {
      this.depSvc.criar(nrSeqCupom, {
        nrSeqCupomDep: dep.nrSeqCupomDep,
        ieTipoDep: dep.ieTipoDep,
        nrDiasValidade: dep.nrDiasValidade,
      }).subscribe({ next: ok, error: fail('dep') });
    }
  }

  private toNum(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private toInt(v: unknown): number | null {
    const n = this.toNum(v);
    return n === null ? null : Math.trunc(n);
  }
}
